import { apiClient, logout, socketService } from "@/api";
import { chatService } from "../services/ChatService";
import * as React from "react";
import { useState, useCallback } from "react";
import { getToken, normalizeRoleNames } from "../utils";
import { useRoleFlags, RoleFlags } from "../hooks/useRoleFlags";

interface AuthContextType extends RoleFlags {
    isAuthenticated: boolean;
    access_token: string;
    signin: (data: any, callback: VoidFunction) => void;
    signout: (callback: VoidFunction) => void;
    roles: string[];
    email: string;
    userId: string;
    user: any;
    permissions: string[];
    isGuest: boolean;
    guestEmail: string;
    guestName: string;
    initializeGuestSession: (email: string, name: string, callback: VoidFunction) => void;
    endGuestSession: (callback: VoidFunction) => void;
    isAvailableForChat: boolean;
    toggleChatAvailability: (available: boolean) => Promise<void>;
    can: (permission: string) => boolean;
    show: (permission: string) => boolean;
    profileImage: string | null;
    setProfileImage: (url: string | null) => void;
}

export const AuthContext = React.createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const isTokenPresent = getToken();
    const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(Boolean(isTokenPresent));
    const [permissions, setPermissions] = React.useState<string[]>([]);
    const [userId, setUserId] = React.useState<any>('')
    const [access_token, setAccessToken] = React.useState<string>(isTokenPresent || '')
    const [roles, setRoles] = React.useState<string[]>([]);
    const [email, setEmail] = React.useState<string>("")
    const [user, setUser] = React.useState<any>({
        user_id: '', email: '', name: '', isAdmin: false
    })
    const [isGuest, setIsGuest] = useState<boolean>(false);
    const [guestEmail, setGuestEmail] = useState<string>('');
    const [guestName, setGuestName] = useState<string>('');
    const [profileImage, setProfileImage] = useState<string | null>(null);
    const [isAvailableForChat, setIsAvailableForChat] = useState<boolean>(true);

    const roleFlags = useRoleFlags(roles);

    const can = useCallback((permission: string): boolean => {
        if (roleFlags.isSuperadmin) return true;
        return permissions.includes(permission);
    }, [permissions, roleFlags.isSuperadmin]);

    const show = useCallback((permission: string): boolean => {
        if (roleFlags.isSuperadmin) return true;
        return permissions.includes(permission);
    }, [permissions, roleFlags.isSuperadmin]);

    const initializeSocket = useCallback((userId: string, token: string) => {
        if (!userId || !token) return;
    
        socketService.initialize(
            userId,
            token,
            {
                processNotification: () => { },
                setNotifications: () => { },
                setNewNotification: () => { },
                setOnlineUsers: () => { },
                onChatMessage: () => { },
                onChatStatusUpdate: () => { },
                onNewChat: () => { },
                onChatStaffJoined: () => { },
                onChatStaffLeft: () => { },
                onChatEnded: () => { },
                onChatTransferred: () => { },
                onStaffAvailabilityChanged: ( ) => { },
                onStaffStatusUpdate: ( ) => { },
                onTicketStatusChange: () => {

                }
            }
        );
    }, []);
    
    

    const initializeGuestSession = useCallback((email: string, name: string, callback: VoidFunction) => {
        setIsGuest(true);
        setGuestEmail(email);
        setGuestName(name);
        socketService.initializeGuest(email, {
            processNotification: () => { },
            setNotifications: () => { },
            setNewNotification: () => { },
            setOnlineUsers: () => { },
            onChatMessage: () => { },
            onChatStatusUpdate: () => { },
            onNewChat: () => { },
            onChatStaffJoined: () => { },
            onChatStaffLeft: () => { },
            onChatEnded: () => { },
            onChatTransferred: () => { },
           
        });
        callback();
    }, []);

    React.useEffect(() => {
        if (isTokenPresent) {
            getRolePermissionList();
        }

        return () => {
            socketService.disconnect();
        };
    }, []);

    const toggleChatAvailability = async (available: boolean) => {
        try {
            await chatService.toggleAvailability(available);
            setIsAvailableForChat(available);
            socketService.socketInstance?.emit('toggle-staff-availability', { available });
        } catch (error) {
            console.error('Failed to toggle chat availability:', error);
        }
    };

    const getRolePermissionList = async () => {
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
        try {
            const { status, data } = await apiClient.post("get-user");
            if (status == 200) {
                const { email, name, roles, user_id, available_for_chat, profile_image } = data;
                const roleNames: string[] = Array.isArray(roles) ? roles : [];
                setEmail(email);
                setUser({ user_id, email, name, isAdmin: roleNames.some(roles => ['Superadmin', 'Admin'].includes(roles)) });
                setAccessToken(access_token);
                setRoles(roleNames);
                setUserId(user_id);
                setPermissions(data.permissions);
                setIsAvailableForChat(available_for_chat);
                setProfileImage(profile_image ?? null);
                initializeSocket(user_id, access_token);
            }
        } catch (error) {
            console.error('Failed to get user data:', error);
        }
    };

    const signin = useCallback((data: any, callback: VoidFunction) => {
        if (data.access_token) {
            apiClient.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
            const { email, name, roles, id: user_id, available_for_chat, profile_image } = data.user;
            const roleNames: string[] = normalizeRoleNames(roles);
            localStorage.setItem("cactoken", data.access_token);
            localStorage.setItem("cacuserid", user_id);
            setIsAuthenticated(true);
            setAccessToken(data.access_token);
            setRoles(roleNames);
            setEmail(email);
            setUserId(user_id);
            setPermissions(data.permissions || []);
            setUser({ user_id, email, name, isAdmin: roleNames.some(roles => ['Superadmin', 'Admin'].includes(roles)) });
            setIsAvailableForChat(available_for_chat ?? true);
            setProfileImage(profile_image ?? null);
            initializeSocket(user_id, data.access_token);

            callback();
        }
    }, [initializeSocket]);

    const signout = useCallback(async (callback: VoidFunction) => {
        try {
            await apiClient.post('/user/set-offline-status');

            socketService.socketInstance?.emit('user-logout', {
                userId: user.user_id,
                isOnline: false,
                lastActivity: new Date().toISOString()
            });
            socketService.disconnect();

            await logout();
            localStorage.removeItem("cactoken");
            sessionStorage.removeItem("cactoken");
            localStorage.clear();
            sessionStorage.clear();
            setRoles([]);
            setEmail("");
            setPermissions([]);
            setAccessToken("");
            setIsAuthenticated(false);
            setUser({ user_id: '', email: '', name: '', isAdmin: false });
            callback();
        } catch (error) {
            console.error('Error during signout:', error);
            localStorage.clear();
            sessionStorage.clear();
            setRoles([]);
            setEmail("");
            setPermissions([]);
            setAccessToken("");
            setIsAuthenticated(false);
            setUser({ user_id: '', email: '', name: '', isAdmin: false });
            callback();
        }
    }, [user]);

    const endGuestSession = useCallback((callback: VoidFunction) => {
        socketService.disconnect();
        setIsGuest(false);
        setGuestEmail('');
        setGuestName('');
        callback();
    }, []);

    const value = {
        isAuthenticated,
        signin,
        signout,
        roles,
        email,
        userId,
        user,
        permissions,
        access_token,
        isGuest,
        guestEmail,
        guestName,
        initializeGuestSession,
        endGuestSession,
        isAvailableForChat,
        toggleChatAvailability,
        can,
        show,
        profileImage,
        setProfileImage,
        ...roleFlags,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
